import { motion } from 'framer-motion';
import { useRef } from 'react';
import FAQSection from '../components/FAQ/FAQSection';
import ServicesSection from '../components/Services/ServicesSection';
import AboutUsSection from '../components/About/AboutUsSection';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';

// Update the Hero section in your Home component
const Home = () => {
  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <div ref={containerRef} className="overflow-hidden">
      {/* Hero Section */}
      <section className="min-h-screen relative flex items-center">
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/90 to-secondary">
          <div className="absolute inset-0 opacity-30">
            <div className="absolute inset-0" style={{
              backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
              backgroundSize: '50px 50px'
            }}></div>
          </div>
        </div>

        {/* Content */}
        <div className="relative max-w-7xl mx-auto px-4 py-24 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Column */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              {/* Badge */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
                className="inline-flex items-center px-4 py-2 bg-white/10 backdrop-blur rounded-full"
              >
                <span className="animate-pulse w-2 h-2 bg-green-400 rounded-full mr-2"></span>
                <span className="text-white text-sm">We're Ensure Quality!</span>
              </motion.div>

              {/* Main Heading */}
              <div className="space-y-4">
                <motion.h1 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight"
                >
                  Building Digital 
                  <span className="block text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-pink-400">
                    Excellence
                  </span>
                </motion.h1>
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="text-xl text-gray-200 max-w-xl"
                >
                  We transform ideas into innovative digital solutions. Specializing in software development, we help businesses like yours build engaging websites, dynamic e-commerce platforms, and custom software tailored to meet your specific needs.
                </motion.p>
              </div>

              {/* CTA Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="flex flex-wrap gap-4"
              >
                <button className="group relative px-8 py-4 bg-white text-primary rounded-full font-semibold overflow-hidden transition-transform hover:scale-105">
                  <a href='/contact'>
                  <span className="relative z-10">Start Your Project</span>
                  </a>
                  <div className="absolute inset-0 bg-gradient-to-r from-yellow-400 to-pink-400 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left"></div>
                </button>
                <button className="group px-8 py-4 border-2 border-white text-white rounded-full font-semibold flex items-center space-x-2 hover:bg-white/10 transition-colors">
                  <span>View Our Work</span>
                  <svg 
                    className="w-5 h-5 group-hover:translate-x-1 transition-transform" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </motion.div>
            </motion.div>

            {/* Right Column - Video Banner */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4 }}
              className="relative"
            >
              <div className="relative aspect-square rounded-2xl overflow-hidden">
                {/* Video Background */}
                <DotLottieReact
                  src="https://lottie.host/1033374e-e849-4d78-9607-414ac6966d70/JbUF0iBoGb.lottie"
                  loop
                  autoplay
                />

                {/* Dot Pattern Overlay */}
                <div className="absolute inset-0 opacity-30">
                  <div className="absolute inset-0" style={{
                    backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
                    backgroundSize: '50px 50px'
                  }}></div>
                </div>

                {/* Floating Elements */}
                <motion.div
                  animate={{
                    y: [-10, 10, -10],
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                  className="absolute top-12 right-8 bg-white rounded-2xl shadow-xl p-4"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-2xl">✓</span>
                    </div>
                    <div>
                      <div className="font-bold">Latest Project</div>
                      <div className="text-sm text-gray-500">Completed Successfully</div>
                    </div>
                  </div>
                </motion.div>

                {/* Stats Card */}
                <motion.div
                  animate={{
                    x: [-10, 10, -10],
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 1
                  }}
                  className="absolute left-8 bottom-1/4 bg-white rounded-2xl shadow-xl p-4"
                >
                  <div className="text-center">
                    <div className="text-3xl font-bold text-primary">10+</div>
                    <div className="text-sm text-gray-500">Software Expertise</div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          animate={{
            y: [0, 10, 0],
          }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white"
        >
          <div className="flex flex-col items-center">
            <span className="text-sm mb-2">Scroll to explore</span>
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </div>
        </motion.div>
      </section>

      {/* Services Section */}
      <ServicesSection />

      {/* About Us Section */}
      <AboutUsSection />

      {/* FAQ Section */}
      <FAQSection />
    </div>
  );
};

export default Home;
